import Calendar from './_calendar.js';
import { smoothScrollTo } from './_scrolling.js';
import { getCSSVariableValue } from './_scrolling.js';

class FormHandler {
  constructor(formSelector) {
    this.form = document.querySelector(formSelector);
    if (!this.form) return;

    this.fieldWrappers = this.form.querySelectorAll('.field');
    this.selects = this.form.querySelectorAll('.select');
    this.submitBtn = this.form.querySelector('.btn');
    this.ajaxUrl = tcc_ajax.ajax_url;

    this._toggleActive = this._toggleActive.bind(this);

    this._initFields();
    this._initSelectDropdowns();
    this._initSteps(); // NEW: multi-step behaviour from server-rendered HTML
    this._bindGlobalClick();
    this._initFormSubmission();

    // Prevent Enter from prematurely submitting on non-final steps
    this.form.addEventListener('keydown', (e) => this._maybeInterceptEnter(e));

    this.form.addEventListener('reset', () => this._handleFormReset());
  }

  /* ---------------- Field chrome ---------------- */
  _toggleActive(wrapper, active) {
    wrapper.classList[active ? 'add' : 'remove']('is-active');
  }

  _initFields() {
    this.fieldWrappers.forEach(wrapper => {
      const input = wrapper.querySelector('input, textarea, select');
      if (!input) return;

      if (input.type === 'text' && input.dataset.calendar === 'true') {
        new Calendar(input);
      }

      this._toggleActive(wrapper, Boolean(input.value));

      if (input.tagName === 'SELECT') {
        if (input.value) wrapper.classList.add('is-active');
      } else {
        input.addEventListener('focus', () => {
          wrapper.classList.add('is-focused');
          this._toggleActive(wrapper, true);
        });
        input.addEventListener('blur', () => {
          wrapper.classList.remove('is-focused');
          this._toggleActive(wrapper, Boolean(input.value));
        });
        input.addEventListener('input', () => this._toggleActive(wrapper, Boolean(input.value)));
      }
    });
  }

  /* ---------------- Custom select ---------------- */
  _initSelectDropdowns() {
    const openDropdown = sel => {
      const btn = sel.querySelector('.select__value');
      const dd = sel.querySelector('.select__dropdown');
      const wp = sel.closest('.field');
      this.selects.forEach(s => { if (s !== sel) closeDropdown(s); });
      sel.classList.add('active');
      btn.setAttribute('aria-expanded', 'true');
      dd.hidden = false;
      wp.classList.add('is-focused');
      this._toggleActive(wp, true);
    };

    const closeDropdown = sel => {
      const btn = sel.querySelector('.select__value');
      const dd = sel.querySelector('.select__dropdown');
      const wp = sel.closest('.field');
      sel.classList.remove('active');
      btn.setAttribute('aria-expanded', 'false');
      dd.hidden = true;
      wp.classList.remove('is-focused');
      const nativeSelect = sel.querySelector('select');
      const isMultiple = nativeSelect && nativeSelect.hasAttribute('multiple');
      this._toggleActive(wp, isMultiple ? Array.from(nativeSelect.selectedOptions).length > 0 : Boolean(nativeSelect.value));
    };

    const toggleDropdown = sel => {
      if (sel.classList.contains('active')) closeDropdown(sel);
      else openDropdown(sel);
    };

    this.selects.forEach(select => {
      const wrapper = select.closest('.field');
      const trigger = select.querySelector('.select__value');
      const triggerSpan = trigger.querySelector('span');
      const dropdown = select.querySelector('.select__dropdown');
      const nativeSelect = select.querySelector('select');
      const options = Array.from(dropdown.querySelectorAll('[role="option"]'));
      const isMultiple = nativeSelect.hasAttribute('multiple');

      const getSelectedValues = () => Array.from(nativeSelect.selectedOptions).map(opt => opt.value);

      const setSelectedValues = (vals) => {
        Array.from(nativeSelect.options).forEach(option => {
          option.selected = vals.includes(option.value);
        });
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      };

      const updateDropdownUI = () => {
        const selectedVals = getSelectedValues();
        options.forEach(optEl => {
          const optVal = optEl.getAttribute('data-value');
          if (selectedVals.includes(optVal)) {
            optEl.classList.add('selected');
            optEl.setAttribute('aria-selected', 'true');
            if (isMultiple && !optEl.querySelector('.icon--tick')) {
              const tick = document.createElement('i');
              tick.className = 'icon icon--tick';
              optEl.appendChild(tick);
            }
            if (!isMultiple) {
              const tick = optEl.querySelector('.icon--tick');
              if (tick) tick.remove();
            }
          } else {
            optEl.classList.remove('selected');
            optEl.setAttribute('aria-selected', 'false');
            const tick = optEl.querySelector('.icon--tick');
            if (tick) tick.remove();
          }
        });
      };

      const updateTriggerUI = () => {
        const selectedVals = getSelectedValues();
        if (selectedVals.length === 0) {
          triggerSpan.textContent = '';
        } else if (selectedVals.length <= 1) {
          triggerSpan.textContent = options
            .filter(optEl => selectedVals.includes(optEl.getAttribute('data-value')))
            .map(optEl => optEl.textContent.trim())
            .join(', ');
        } else {
          triggerSpan.textContent = `${selectedVals.length} selected`;
        }
      };

      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      dropdown.setAttribute('role', 'listbox');
      dropdown.hidden = true;
      options.forEach(opt => {
        opt.setAttribute('tabindex', '-1');
      });

      trigger.addEventListener('click', e => {
        e.stopPropagation();
        toggleDropdown(select);
      });

      trigger.addEventListener('keydown', e => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            openDropdown(select);
            options[0]?.focus();
            break;
          case 'ArrowUp':
            e.preventDefault();
            openDropdown(select);
            options[options.length - 1]?.focus();
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            toggleDropdown(select);
            break;
        }
      });

      options.forEach((opt, idx) => {
        opt.addEventListener('click', e => {
          e.preventDefault();
          const val = opt.getAttribute('data-value');
          if (isMultiple) {
            let selectedVals = getSelectedValues();
            if (selectedVals.includes(val)) {
              selectedVals = selectedVals.filter(v => v !== val);
            } else {
              selectedVals.push(val);
            }
            setSelectedValues(selectedVals);
            updateDropdownUI();
            updateTriggerUI();
          } else {
            nativeSelect.value = val;
            nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            triggerSpan.textContent = opt.textContent;
            closeDropdown(select);
          }
        });

        opt.addEventListener('keydown', e => {
          let next, prev;
          switch (e.key) {
            case 'ArrowDown':
              e.preventDefault();
              next = options[idx + 1] || options[0];
              next.focus();
              break;
            case 'ArrowUp':
              e.preventDefault();
              prev = options[idx - 1] || options[options.length - 1];
              prev.focus();
              break;
            case 'Enter':
            case ' ':
              e.preventDefault();
              opt.click();
              if (!isMultiple) closeDropdown(select);
              break;
            case 'Escape':
              e.preventDefault();
              trigger.focus();
              closeDropdown(select);
              break;
          }
        });
      });

      updateDropdownUI();
      updateTriggerUI();
      nativeSelect.addEventListener('change', () => {
        updateDropdownUI();
        updateTriggerUI();
      });
    });

    this._selectHelpers = { openDropdown, closeDropdown, toggleDropdown };
  }

  _bindGlobalClick() {
    document.addEventListener('click', (e) => {
      if (this.selects && this.selects.length) {
        if (!e.target.closest('.select')) {
          if (this._selectHelpers && this._selectHelpers.closeDropdown) {
            this.selects.forEach(s => this._selectHelpers.closeDropdown(s));
          }
        }
      }
    });
  }

  /* ---------------- Multi-step (server-rendered) ---------------- */
  _initSteps() {
    // Server renders fieldsets .form__step (first visible, others hidden)
    this.stepsContainer = this.form.querySelector('.form__steps') || this.form.querySelector('.form__container');
    this.stepFieldsets = Array.from(this.form.querySelectorAll('.form__step'));
    this.stepCount = this.stepFieldsets.length;

    if (!this.stepCount) return;

    // Determine current step: prefer .is-current, else first visible, else 0
    let currentIndex = this.stepFieldsets.findIndex(fs => fs.classList.contains('is-current'));
    if (currentIndex < 0) {
      currentIndex = this.stepFieldsets.findIndex(fs => !fs.hasAttribute('hidden'));
    }
    this.currentStep = currentIndex >= 0 ? currentIndex : 0;

    // Normalise hidden & aria
    this.stepFieldsets.forEach((fs, idx) => {
      const isCurrent = idx === this.currentStep;
      fs.classList.toggle('is-current', isCurrent);
      if (isCurrent) {
        fs.removeAttribute('hidden');
        fs.setAttribute('aria-hidden', 'false');
      } else {
        fs.setAttribute('hidden', '');
        fs.setAttribute('aria-hidden', 'true');
      }
    });

    // Live region for announcements
    this._ensureLiveRegion();
    this._announceStep();

    // Delegate Back/Next clicks
    this.form.addEventListener('click', (e) => {
      const backBtn = e.target.closest('.form__back');
      const nextBtn = e.target.closest('.form__next');
      const skipBtn = e.target.closest('.form__skip'); // 👈 new

      if (backBtn) {
        e.preventDefault();
        this._goToStep(this.currentStep - 1);
      }
      if (nextBtn) {
        e.preventDefault();
        if (this._validateCurrentStep()) this._goToStep(this.currentStep + 1);
      }
      if (skipBtn) {
        e.preventDefault();
        this._goToStep(this.currentStep + 1); // 👈 no validation
      }
    });
  }

  _ensureLiveRegion() {
    if (this.liveRegion) return;
    this.liveRegion = this.form.querySelector('.visually-hidden[aria-live]');
    if (!this.liveRegion) {
      this.liveRegion = document.createElement('div');
      this.liveRegion.className = 'visually-hidden';
      this.liveRegion.setAttribute('aria-live', 'polite');
      this.form.prepend(this.liveRegion);
    }
  }

  _announceStep() {
    if (!this.stepCount) return;
    const num = this.currentStep + 1;
    const heading = this.stepFieldsets[this.currentStep].querySelector('.form__step-title');
    const title = heading ? heading.textContent.trim() : `Step ${num}`;
    this.liveRegion.textContent = `Step ${num} of ${this.stepCount}: ${title}`;
  }

  _goToStep(index) {
    if (!this.stepCount) return;
    if (index < 0 || index >= this.stepCount || index === this.currentStep) return;

    const prev = this.stepFieldsets[this.currentStep];
    const next = this.stepFieldsets[index];

    // Hide previous
    prev.classList.remove('is-current');
    prev.setAttribute('hidden', '');
    prev.setAttribute('aria-hidden', 'true');

    // Show next
    next.classList.add('is-current');
    next.removeAttribute('hidden');
    next.setAttribute('aria-hidden', 'false');

    this.currentStep = index;

    // Focus management: heading or first input
    const heading = next.querySelector('.form__step-title, [id^="step-"][id$="-title"]');
    if (heading) heading.focus?.();
    else {
      const firstInput = next.querySelector('input, select, textarea, button');
      firstInput?.focus?.();
    }

    // announcement
    this._announceStep();

    // Scroll into view (account for sticky header)
    const navigationYOffset = getCSSVariableValue('--navigationYOffset');
    const offset = next.getBoundingClientRect().top + window.pageYOffset;
    const scrollTo = offset - navigationYOffset - 40;
    if ('scrollBehavior' in document.documentElement.style && !/Safari/.test(navigator.userAgent)) {
      window.scrollTo({ top: scrollTo, behavior: 'smooth' });
    } else {
      smoothScrollTo(scrollTo, 500);
    }
  }

  _validateCurrentStep() {
    if (!this.stepCount) return true;
    const fs = this.stepFieldsets[this.currentStep];
    if (!fs) return true;

    const controls = Array.from(fs.querySelectorAll('input, select, textarea'))
      .filter(el => el.type !== 'hidden' && !el.disabled);

    // Use native validity; report first invalid
    for (const el of controls) {
      if (el.willValidate && !el.checkValidity()) {
        // Make sure any custom UI (like your field wrappers) show active state
        el.reportValidity?.();
        // Also mark wrapper active for visibility
        const field = el.closest('.field, .checkbox');
        field && field.classList.add('field--error');
        return false;
      }
    }
    return true;
  }

  _isOnFinalStep() {
    return this.stepCount ? this.currentStep === this.stepCount - 1 : true;
  }

  _maybeInterceptEnter(e) {
    if (e.key !== 'Enter') return;
    const target = e.target;
    if (!target || target.tagName === 'TEXTAREA') return;
    // If not on final step, Enter should attempt "Next" instead of submit
    if (!this._isOnFinalStep()) {
      e.preventDefault();
      // Validate and advance if valid
      if (this._validateCurrentStep()) this._goToStep(this.currentStep + 1);
    }
  }

  /* ---------------- Submit (AJAX) ---------------- */
  _initFormSubmission() {
    if (!this.form.classList.contains('form--builder')) return;

    this.form.addEventListener('submit', async e => {
      // Only allow submit on final step
      if (!this._isOnFinalStep()) {
        e.preventDefault();
        if (this._validateCurrentStep()) this._goToStep(this.currentStep + 1);
        return;
      }

      e.preventDefault();

      // Final full-form validity gate (native)
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      const recaptcha = this.form.querySelector('[name="g-recaptcha-response"]');
      if (recaptcha && !recaptcha.value) return;

      const formData = new FormData(this.form);
      formData.append('form_id', this.form.id);
      formData.append('action', 'form_response');

      // Find the final step's submit button (safer than first .btn found)
      const finalStep = this.stepCount ? this.stepFieldsets[this.stepCount - 1] : this.form;
      const submitBtn = finalStep.querySelector('input[type="submit"], button[type="submit"]') || this.submitBtn;

      if (submitBtn) {
        submitBtn.disabled = true;
      }
      const originalText = submitBtn ? (submitBtn.value || submitBtn.textContent) : null;
      if (submitBtn) submitBtn.value = submitBtn.textContent = 'Sending...';

      try {
        const res = await fetch(this.ajaxUrl, {
          method: 'POST',
          body: formData
        });
        const result = await res.json();
        if (result && typeof result === 'object') {
          result.success ? this._onSuccess(result.success) : this._onError(result.error);
        } else {
          console.error('Unexpected response:', result);
        }
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.value = submitBtn.textContent = originalText ?? '';
        }
      }
    });
  }

  _onError(response) {
    this._clearNotices();

    Object.values(response.fields).forEach(field => {
      if (['hidden', 'honeypot'].includes(field.type)) return;
      const input = this.form.querySelector(`[name="${field.name}"]`);
      if (!input) return;
      const container = input.closest('.field, .checkbox');
      if (field.error) {
        container.classList.add(field.type === 'checkbox' ? 'checkbox--error' : 'field--error');
        if (field.type !== 'checkbox') {
          let errEl = container.querySelector('.field__error') || document.createElement('span');
          errEl.className = 'field__error';
          errEl.textContent = field.error;
          container.appendChild(errEl);
        }
      } else {
        container.classList.remove('field--error', 'checkbox--error');
        const errEl = container.querySelector('.field__error');
        if (errEl) errEl.remove();
      }
    });

    // Jump to first step that contains an error
    const firstError = this.form.querySelector('.field--error, .checkbox--error');
    if (firstError) {
      const step = firstError.closest('.form__step');
      if (step) {
        const idx = this.stepFieldsets.indexOf(step);
        if (idx >= 0 && idx !== this.currentStep) this._goToStep(idx);
      }
    }

    if (response.message && response.status === 'error') {
      const div = document.createElement('div');
      div.className = 'form__error';
      div.textContent = response.message;
      this.form.prepend(div);
      this._scrollToMessage(div);
    }

    if (window.grecaptcha) {
      grecaptcha.reset();
    }
  }

  _onSuccess(response) {
    this._clearNotices();

    // Tell Tag Manager an enquiry was actually sent (the server accepted it). The site shows an
    // inline success message rather than a thank-you page, so this is the only success signal.
    // form_id is the form-builder post ID (live: 5481 = Experience form, 747 = Contact form).
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'enquiry_sent', form_id: this.form.id || null });

    this.form.querySelectorAll('.field--error, .checkbox--error')
      .forEach(el => el.classList.remove('field--error', 'checkbox--error'));
    this.form.querySelectorAll('.field__error').forEach(el => el.remove());

    Object.values(response.fields).forEach(field => {
      if (!['hidden', 'honeypot'].includes(field.type)) {
        const input = this.form.querySelector(`[name="${field.name}"]`);
        if (input) { input.value = ''; input.checked = false; }
      }
    });

    if (response.message && response.status === 'success') {
      const div = document.createElement('div');
      div.className = 'form__success';
      div.textContent = response.message;
      this.form.prepend(div);
      this._scrollToMessage(div);
    }
  }

  _handleFormReset() {
    setTimeout(() => {
      // Reset field chrome
      this.fieldWrappers.forEach(wrapper => {
        const input = wrapper.querySelector('input, textarea, select');
        if (!input) return;
        this._toggleActive(wrapper, Boolean(input.value));
        wrapper.classList.remove('is-focused');
      });

      // Reset custom selects UI
      this.selects.forEach(select => {
        const nativeSelect = select.querySelector('select');
        const trigger = select.querySelector('.select__value span');
        const dropdown = select.querySelector('.select__dropdown');
        const options = Array.from(dropdown.querySelectorAll('[role="option"]'));
        const isMultiple = nativeSelect.hasAttribute('multiple');
        const getSelectedValues = () => Array.from(nativeSelect.selectedOptions).map(opt => opt.value);

        const selectedVals = getSelectedValues();
        options.forEach(optEl => {
          const optVal = optEl.getAttribute('data-value');
          if (selectedVals.includes(optVal)) {
            optEl.classList.add('selected');
            optEl.setAttribute('aria-selected', 'true');
            if (isMultiple && !optEl.querySelector('.icon--tick')) {
              const tick = document.createElement('i');
              tick.className = 'icon icon--tick';
              optEl.appendChild(tick);
            }
            if (!isMultiple) {
              const tick = optEl.querySelector('.icon--tick');
              if (tick) tick.remove();
            }
          } else {
            optEl.classList.remove('selected');
            optEl.setAttribute('aria-selected', 'false');
            const tick = optEl.querySelector('.icon--tick');
            if (tick) tick.remove();
          }
        });

        if (selectedVals.length === 0) {
          trigger.textContent = '';
        } else if (selectedVals.length <= 1) {
          trigger.textContent = options
            .filter(optEl => selectedVals.includes(optEl.getAttribute('data-value')))
            .map(optEl => optEl.textContent.trim())
            .join(', ');
        } else {
          trigger.textContent = `${selectedVals.length} selected`;
        }

        select.classList.remove('active');
        const field = select.closest('.field');
        if (field) field.classList.remove('is-focused');
      });

      // Return to first step
      if (this.stepCount) this._goToStep(0);
    }, 1);
  }

  _scrollToMessage(el) {
    if (!el) return;
    setTimeout(() => {
      const navigationYOffset = getCSSVariableValue('--navigationYOffset');
      const offset = el.getBoundingClientRect().top + window.pageYOffset;
      const scrollTo = offset - navigationYOffset - 40;
      if ('scrollBehavior' in document.documentElement.style && !/Safari/.test(navigator.userAgent)) {
        window.scrollTo({ top: scrollTo, behavior: 'smooth' });
      } else {
        smoothScrollTo(scrollTo, 500);
      }
    }, 500);
  }

  _clearNotices() {
    this.form.querySelectorAll('.form__error, .form__success').forEach(n => n.remove());
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new FormHandler('form');
});